import React, { useMemo, useState } from 'react';
import { Table } from '@rd-web-markets/shared/dist/util';
import Pagination from '@rd-web-markets/shared/dist/util/Pagination';
import { Card } from 'react-bootstrap';
import claimReductionRatesService from '../../services/claim/claim_reduction_rates.service';
import CostSectionRateReductionForm from './CostSectionRateReductionForm';
import { COST_REDUCTION_CATEGORIES } from './costReductionCategories';
import { useShowToast } from '@rd-web-markets/shared/dist/util/toast/useToast';
import * as QueryString from 'query-string';
import { useSelector } from 'react-redux';
const reductionCategoryLabelsByValue = COST_REDUCTION_CATEGORIES.reduce((acc, reductionCategory) => {
  acc[reductionCategory.value] = reductionCategory.label;
  return acc;
}, {});
const CLAIM_REDUCTION_RATE_APPLY_TERMINAL_STATUSES = ['applied_successfully', 'applied_unsuccessfully'];
const getDefaultFormState = claim => {
  return {
    reductionCategory: 'ClaimGroup',
    categoryToReduce: String(claim.claim_group.id),
    categoryToReduceName: claim.claim_group.name,
    reductionRate: ''
  };
};
const CostSectionRateReduction = _ref => {
  let {
    claim,
    costType
  } = _ref;
  const showToast = useShowToast();
  const user = useSelector(state => state.auth.user);
  const isCustomer = user?.account_type === 'customer';
  const claimId = claim.id;
  const [queryParams, setQueryParams] = useState({
    page: 1,
    ...QueryString.parse(location.search)
  });
  const {
    claimReductionRates,
    createClaimReductionRate,
    fetchClaimReductionRates,
    updateClaimReductionRate,
    deleteClaimReductionRate,
    totalPages
  } = claimReductionRatesService.useCrud({
    claimId,
    cost_category_type: costType,
    initialQueryParams: queryParams
  });
  const [isFormVisible, setIsFormVisible] = useState(false);
  const [editingRowId, setEditingRowId] = useState(null);
  const [formState, setFormState] = useState(getDefaultFormState(claim));
  const [claimReductionRateState, setClaimReductionRateState] = useState(null);
  const [claimReductionRatePollOperation, setClaimReductionRatePollOperation] = useState(null);
  const [isClaimReductionRatePolling, setIsClaimReductionRatePolling] = useState(false);
  const {
    startPolling,
    stopPolling
  } = claimReductionRatesService.usePollClaimReductionRate({
    claimId,
    costCategoryType: costType,
    setClaimReductionRate: setClaimReductionRateState
  });
  const parsedReductionRate = Number(formState.reductionRate);
  const isSaveDisabled = !formState.categoryToReduce || formState.reductionRate === '' || Number.isNaN(parsedReductionRate);
  const headers = useMemo(() => {
    return [{
      text: 'Select Reduction Category',
      sortable: true,
      filterableByText: true,
      valueFnForSortAndFilter: tableRow => tableRow.columns[0] || ''
    }, {
      text: 'Category To Reduce',
      sortable: true,
      filterableByText: true,
      valueFnForSortAndFilter: tableRow => tableRow.columns[1] || ''
    }, {
      text: 'Reduction Rate',
      sortable: true,
      filterableByText: true,
      valueFnForSortAndFilter: tableRow => tableRow.columns[2] || ''
    }];
  }, []);
  const tableRows = useMemo(() => {
    return claimReductionRates.map(claimReductionRate => {
      if (claimReductionRateState?.id === claimReductionRate.id && claimReductionRateState.deleted) {
        return null;
      }
      let isPending = !CLAIM_REDUCTION_RATE_APPLY_TERMINAL_STATUSES.includes(claimReductionRate.update_status);
      if (claimReductionRateState?.id === claimReductionRate.id) {
        isPending = !CLAIM_REDUCTION_RATE_APPLY_TERMINAL_STATUSES.includes(claimReductionRateState.update_status) || claimReductionRateState.set_for_destruction;
      }
      return {
        key: claimReductionRate.id,
        data: {
          object: claimReductionRate
        },
        columns: [reductionCategoryLabelsByValue[claimReductionRate.object_type], claimReductionRate.object_name, `${claimReductionRate.reduction_rate_percentage}%`],
        pending: isPending
      };
    }).filter(Boolean);
  }, [claimReductionRates, claimReductionRateState]);
  const changePageTo = pageNumber => {
    window.scrollTo(0, 0);
    setQueryParams({
      ...queryParams,
      page: pageNumber
    });
  };
  const shouldPollAfterApply = claimReductionRate => {
    const shouldContinuePolling = !CLAIM_REDUCTION_RATE_APPLY_TERMINAL_STATUSES.includes(claimReductionRate.update_status);
    if (!shouldContinuePolling) {
      setIsClaimReductionRatePolling(false);
      showToast({
        message: 'Claim reduction rate has been applied successfully!',
        variant: 'success'
      });
    }
    return shouldContinuePolling;
  };
  const startApplyingClaimReductionRatePolling = claimReductionRate => {
    stopPolling();
    setClaimReductionRateState(claimReductionRate);
    setClaimReductionRatePollOperation('apply');
    setIsClaimReductionRatePolling(true);
    startPolling({
      claimReductionRateId: claimReductionRate.id,
      shouldPollCallback: shouldPollAfterApply
    });
  };
  const startDestroyingClaimReductionRatePolling = claimReductionRate => {
    stopPolling();
    setClaimReductionRateState(claimReductionRate);
    setClaimReductionRatePollOperation('destroy');
    setIsClaimReductionRatePolling(true);
    console.log();
    startPolling({
      claimReductionRateId: claimReductionRate.id,
      shouldPollCallback: () => true
    });
  };
  const openCreateForm = () => {
    setEditingRowId(null);
    setFormState(getDefaultFormState(claim));
    setIsFormVisible(true);
  };
  const onReductionCategoryChange = event => {
    const nextReductionCategory = event.target.value;
    const defaultCategoryToReduceByReductionCategory = {
      ClaimGroup: {
        categoryToReduce: String(claim.claim_group.id),
        categoryToReduceName: claim.claim_group.name
      },
      'UkSection::SubsidiaryCompany': {
        categoryToReduce: '',
        categoryToReduceName: ''
      },
      BusinessUnit: {
        categoryToReduce: '',
        categoryToReduceName: ''
      },
      CommercialProject: {
        categoryToReduce: '',
        categoryToReduceName: ''
      },
      ClaimProjectReport: {
        categoryToReduce: '',
        categoryToReduceName: ''
      }
    };
    setFormState({
      ...formState,
      reductionCategory: nextReductionCategory,
      categoryToReduce: defaultCategoryToReduceByReductionCategory[nextReductionCategory].categoryToReduce,
      categoryToReduceName: defaultCategoryToReduceByReductionCategory[nextReductionCategory].categoryToReduceName
    });
  };
  const saveReductionRate = async () => {
    const claimReductionRate = {
      reduction_rate_percentage: parsedReductionRate,
      object_type: formState.reductionCategory,
      object_id: formState.categoryToReduce,
      cost_category_type: costType
    };
    let savedClaimReductionRate;
    if (editingRowId) {
      savedClaimReductionRate = await updateClaimReductionRate({
        id: editingRowId,
        ...claimReductionRate
      });
    } else {
      savedClaimReductionRate = await createClaimReductionRate(claimReductionRate);
    }
    await fetchClaimReductionRates(queryParams);
    if (savedClaimReductionRate) {
      startApplyingClaimReductionRatePolling(savedClaimReductionRate);
    }
    setEditingRowId(null);
    setFormState(getDefaultFormState(claim));
    setIsFormVisible(false);
  };
  const editReductionRate = (_index, tableRow) => {
    const row = tableRow.data.object;
    setEditingRowId(row.id);
    setFormState({
      reductionCategory: row.object_type,
      categoryToReduce: String(row.object_id),
      categoryToReduceName: tableRow.columns[1],
      reductionRate: String(row.reduction_rate_percentage)
    });
    setIsFormVisible(true);
  };
  const deleteReductionRate = async (_index, tableRow) => {
    const claimReductionRate = tableRow.data.object;
    const deletedClaimReductionRate = await deleteClaimReductionRate(claimReductionRate);
    startDestroyingClaimReductionRatePolling(deletedClaimReductionRate);
  };
  const cancelReductionRate = () => {
    setEditingRowId(null);
    setFormState(getDefaultFormState(claim));
    setIsFormVisible(false);
  };
  return /*#__PURE__*/React.createElement(Card, null, /*#__PURE__*/React.createElement(Card.Body, null, /*#__PURE__*/React.createElement("h4", {
    className: "mb-3"
  }, "Reduction Rate"), isFormVisible && !isCustomer && /*#__PURE__*/React.createElement(CostSectionRateReductionForm, {
    claim: claim,
    formState: formState,
    setFormState: setFormState,
    onReductionCategoryChange: onReductionCategoryChange,
    saveReductionRate: saveReductionRate,
    cancelReductionRate: cancelReductionRate,
    isSaveDisabled: isSaveDisabled,
    isEditing: !!editingRowId
  }), /*#__PURE__*/React.createElement(Table, {
    headers: headers,
    rows: tableRows,
    onEdit: isCustomer ? undefined : editReductionRate,
    onDelete: isCustomer ? undefined : deleteReductionRate,
    onAddRow: isCustomer ? null : openCreateForm,
    showControls: !isCustomer,
    opts: {
      size: 'sm',
      className: 'mt-3'
    }
  }), tableRows.length === 0 && /*#__PURE__*/React.createElement("div", {
    className: "text-center text-muted py-4"
  }, "No results found"), /*#__PURE__*/React.createElement("div", {
    className: "mt-3"
  }, /*#__PURE__*/React.createElement(Pagination, {
    currentPage: Number(queryParams.page),
    totalPages: totalPages,
    handlePageChange: changePageTo
  }))));
};
export default CostSectionRateReduction;